import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink } from 'react-router-dom'

import { GooglePlayIcon } from '@/components/GooglePlayIcon/GooglePlayIcon'
import { LOGO_PUBLIC_SRC } from '@/config/branding'
import { appLinks } from '@/config/links'

export function Navbar() {
  const { t } = useTranslation()
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/support', label: t('nav.support') },
    { to: '/privacy-policy', label: t('nav.privacy') },
    { to: '/terms-of-service', label: t('nav.terms') },
  ]

  return (
    <header
      className="fixed top-0 right-0 left-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(240, 244, 255, 0.8)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: scrolled ? '1px solid rgba(26, 86, 255, 0.08)' : '1px solid transparent',
        boxShadow: scrolled ? '0 2px 20px rgba(26, 86, 255, 0.06)' : 'none',
      }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src={LOGO_PUBLIC_SRC}
              alt={t('common.brandAlt')}
              className="h-8 w-8 rounded-xl object-cover"
              width={32}
              height={32}
            />
            <span
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 900,
                fontSize: '20px',
                color: '#0D1B3E',
                letterSpacing: '-0.02em',
              }}
            >
              {t('common.appName')}
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-2 text-sm transition-all duration-200 ${
                    isActive ? '' : ''
                  }`
                }
                style={({ isActive }) => ({
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? '#1A56FF' : '#6B7A99',
                  background: isActive ? 'rgba(26, 86, 255, 0.07)' : 'transparent',
                })}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            <a
              href={appLinks.googlePlay}
              target="_blank"
              rel="noopener noreferrer"
              className="google-play-badge"
              style={{ fontSize: '13px' }}
            >
              <GooglePlayIcon size={18} />
              <div>
                <div style={{ fontSize: '9px', opacity: 0.7, lineHeight: 1 }}>{t('common.getItOn')}</div>
                <div style={{ fontSize: '14px', fontWeight: 700, fontFamily: 'DM Sans', lineHeight: 1.2 }}>
                  {t('common.googlePlay')}
                </div>
              </div>
            </a>
          </div>

          <button
            type="button"
            className="rounded-lg p-2 md:hidden"
            style={{ background: 'rgba(26, 86, 255, 0.07)' }}
            aria-label={t('nav.openMenu')}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <div className="flex w-5 flex-col gap-1.5">
              <div
                className="h-0.5 rounded-full transition-all duration-200"
                style={{
                  background: '#0D1B3E',
                  transform: menuOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none',
                }}
              />
              <div
                className="h-0.5 rounded-full transition-all duration-200"
                style={{
                  background: '#0D1B3E',
                  opacity: menuOpen ? 0 : 1,
                }}
              />
              <div
                className="h-0.5 rounded-full transition-all duration-200"
                style={{
                  background: '#0D1B3E',
                  transform: menuOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none',
                }}
              />
            </div>
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div
          className="border-t md:hidden"
          style={{
            background: 'rgba(255, 255, 255, 0.98)',
            borderColor: 'rgba(26, 86, 255, 0.08)',
          }}
        >
          <div className="space-y-1 px-4 py-4">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} onClick={() => setMenuOpen(false)}>
                <span
                  className="block rounded-xl px-4 py-3 text-sm"
                  style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500, color: '#0D1B3E' }}
                >
                  {link.label}
                </span>
              </Link>
            ))}
            <div className="pt-2">
              <a
                href={appLinks.googlePlay}
                target="_blank"
                rel="noopener noreferrer"
                className="google-play-badge flex w-full justify-center"
              >
                <GooglePlayIcon size={18} />
                <div>
                  <div style={{ fontSize: '9px', opacity: 0.7, lineHeight: 1 }}>{t('common.getItOn')}</div>
                  <div style={{ fontSize: '14px', fontWeight: 700, fontFamily: 'DM Sans', lineHeight: 1.2 }}>
                    {t('common.googlePlay')}
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  )
}
